import {
  FilterOnEmptyState,
  LoadingSpinner,
  NoFlyersAvailableEmptyState,
  NoFlyersAvailableSectionEmptyState,
  ToggleViewControl,
} from '@/components';
import { APP_ROUTES, FlyerType, SubscriptionTypeEnum } from '@/config/constants';
import AuthenticationContext from '@/context/AuthenticationContext';
import FlyersContext from '@/context/FlyersContext';
import { BodyLargeEmphasized, ButtonFlatMediumEmphasized } from '@/design-system';
import { districtVar, flyerboardActiveFiltersVar } from '@/graphql/state';
import { AllFlyerFilters } from '@/graphql/types';
import {
  useFlyerboardPagination,
  useNavigateWithTracking,
  useSchools,
  useURLSearchParams,
} from '@/hooks';
import colors from '@/theme/colors';
import { useReactiveVar } from '@apollo/client';
import { useContext, useMemo, useRef, useState } from 'react';
import styles from './FlyerboardPagination.styles';
import { FlyerList } from './FlyerList';
import { OtherSchoolFlyers } from './OtherSchoolFlyers';
import { TrackingEvents, TrackingStage } from '@/config/tracking';

type Props = {
  trackId: string;
  flyerboardFilters: AllFlyerFilters;
  flyerType: string;
  audienceId?: string;
  districtId?: string | null;
  trackingEventFrom: string;
  className?: string;
};

export const FlyerboardPagination: React.FC<Props> = ({
  trackId,
  flyerboardFilters,
  flyerType,
  audienceId,
  districtId,
  trackingEventFrom,
  className,
}) => {
  const navigateWithTracking = useNavigateWithTracking();
  const [hasOtherSchoolFlyers, setHasOtherSchoolFlyers] = useState<boolean>(true);
  const ref = useRef<HTMLDivElement | null>(null);
  const flyerboardActiveFilters = useReactiveVar(flyerboardActiveFiltersVar);
  const district = useReactiveVar(districtVar);
  const { isAuthenticated } = useContext(AuthenticationContext);
  const {
    activeTab,
    onChangeFlyerType: _onChangeFlyerType,
    isSchoolTabSelected,
  } = useContext(FlyersContext);
  const { hasMultipleChildren, mySchoolId, subscriptions } = useSchools();

  const { audienceType } = useURLSearchParams();

  const { isPaginationEnd, flyers, loading, total, dataNotFound, fetchMoreFlyers } =
    useFlyerboardPagination({
      flyerboardFilters,
      flyerType,
      audienceId,
      districtId,
    });

  const showCommunityHeader =
    !flyerboardFilters.isNew && flyerType === FlyerType.community && !loading;

  const isFiltering = useMemo(
    () => flyerboardActiveFilters.grades.length > 0 || flyerboardActiveFilters.types.length > 0,
    [flyerboardActiveFilters]
  );

  const resetFilters = () => {
    flyerboardActiveFiltersVar({
      grades: [],
      types: [],
    });
  };

  const EmptyState = () => {
    if (!loading && dataNotFound) {
      if (isFiltering) {
        return <FilterOnEmptyState onClick={resetFilters} />;
      }

      if (
        flyerboardFilters.isNew ||
        trackId === 'districtFlyerboard' ||
        (flyerType === FlyerType.community && !hasOtherSchoolFlyers)
      ) {
        return <NoFlyersAvailableEmptyState />;
      }

      return flyerType === FlyerType.school ? (
        <NoFlyersAvailableEmptyState />
      ) : (
        <NoFlyersAvailableSectionEmptyState />
      );
    }

    return null;
  };

  const viewAllFlyerLabel = `View All ${activeTab} Flyers`;

  const trackingFrom = {
    stage_from: TrackingStage.discovery,
    event_from: TrackingEvents.viewFlyerSet,
    event_detail_from: trackingEventFrom,
  }

  const onViewAllFlyerClick = () => {
    let flyerboardParams = '';
    if (isSchoolTabSelected) {
      flyerboardParams = `audienceId=${
        hasMultipleChildren ? mySchoolId : subscriptions[0].id
      }&audienceType=${activeTab}&tab=${activeTab}`;
    } else {
      flyerboardParams = `audienceId=${district?.districtId}&audienceType=${SubscriptionTypeEnum.district}&tab=${activeTab}`;
    }

    navigateWithTracking(`${APP_ROUTES.exploreAllFlyers}?${flyerboardParams}`, trackingFrom);
  };

  return (
    <main className={styles.container} ref={ref}>
      <div className={styles.section}>
        {loading && flyers.length === 0 && <LoadingSpinner />}
        <div className={styles.communityHeader}>
          {showCommunityHeader && (
            <BodyLargeEmphasized color={colors.slate}>
              Community Through My School(s)
            </BodyLargeEmphasized>
          )}
          {!loading && flyers.length > 0 && (
            <div className={styles.buttonGrid}>
              <ToggleViewControl trackingFrom={trackingFrom} />
            </div>
          )}
        </div>
        <EmptyState />
        {flyers.length > 0 && (
          <>
            <FlyerList
              flyers={flyers}
              nextPage={fetchMoreFlyers}
              total={total}
              trackId={trackId}
              audienceId={audienceId}
              districtId={districtId}
              trackingEventFrom={trackingEventFrom}
              className={className}
            />
            {flyerboardFilters.isNew && isPaginationEnd && (
              <div className={styles.viewAllFlyersContainer}>
                <ButtonFlatMediumEmphasized
                  onClick={onViewAllFlyerClick}
                  id="view-all-flyers-button"
                >
                  {viewAllFlyerLabel}
                </ButtonFlatMediumEmphasized>
              </div>
            )}
          </>
        )}
      </div>
      {flyerType === FlyerType.community &&
        !flyerboardFilters.isNew &&
        isPaginationEnd &&
        isAuthenticated && (
          <OtherSchoolFlyers
            flyerType={flyerType}
            flyerboardFilters={flyerboardFilters}
            trackId={trackId}
            audienceId={audienceId}
            setHasOtherSchoolFlyers={setHasOtherSchoolFlyers}
            hasMyCommSchoolFlyers={!dataNotFound}
          />
        )}
    </main>
  );
};
